/**
 * Analytical biquad filter magnitude response.
 *
 * Used to model the effect of peaking EQ filters during the
 * fitting process and for plotting the resulting EQ curve.
 *
 * Reference: Audio EQ Cookbook by Robert Bristow-Johnson
 * https://www.w3.org/2011/audio/audio-eq-cookbook.html
 */

import type { EqBand } from "../types";

/**
 * Compute the magnitude response (in dB) of a peaking EQ biquad
 * at a given frequency.
 *
 * @param f - evaluation frequency in Hz
 * @param fc - center frequency in Hz
 * @param gain - gain in dB
 * @param q - quality factor
 * @returns magnitude response in dB at frequency f
 */
export function peakingMagnitudeDb(
  f: number,
  fc: number,
  gain: number,
  q: number
): number {
  if (gain === 0) return 0;

  const A = Math.pow(10, gain / 40); // sqrt of linear gain
  const w0 = (2 * Math.PI * fc);
  const w = (2 * Math.PI * f);

  // Use the analog prototype transfer function:
  // H(s) = (s^2 + s*(A/Q) + 1) / (s^2 + s/(A*Q) + 1)
  // where s = j*w/w0
  const ratio = w / w0;
  const ratioSq = ratio * ratio;

  const numReal = 1 - ratioSq;
  const numImag = ratio * A / q;
  const denReal = 1 - ratioSq;
  const denImag = ratio / (A * q);

  const numMagSq = numReal * numReal + numImag * numImag;
  const denMagSq = denReal * denReal + denImag * denImag;

  if (denMagSq === 0) return gain;

  return 10 * Math.log10(numMagSq / denMagSq);
}

/**
 * Compute the magnitude response (in dB) of a low-shelf EQ biquad
 * at a given frequency.
 *
 * Reference: Audio EQ Cookbook (shelf analog prototype).
 */
export function lowShelfMagnitudeDb(
  f: number,
  fc: number,
  gain: number,
  q: number
): number {
  if (gain === 0) return 0;
  if (f <= 0) return gain;

  const A = Math.pow(10, gain / 40);
  const r = f / fc;
  const rSq = r * r;

  const numReal = A - rSq;
  const numImag = (Math.sqrt(A) / q) * r;
  const denReal = 1 - A * rSq;
  const denImag = (Math.sqrt(A) / q) * r;

  const numMagSq = numReal * numReal + numImag * numImag;
  const denMagSq = denReal * denReal + denImag * denImag;

  if (denMagSq === 0) return gain;

  return 20 * Math.log10(A) + 10 * Math.log10(numMagSq / denMagSq);
}

/**
 * Compute the magnitude response (in dB) of a high-shelf EQ biquad
 * at a given frequency.
 *
 * Reference: Audio EQ Cookbook (shelf analog prototype).
 */
export function highShelfMagnitudeDb(
  f: number,
  fc: number,
  gain: number,
  q: number
): number {
  if (gain === 0) return 0;

  const A = Math.pow(10, gain / 40);
  const r = f / fc;
  const rSq = r * r;

  const numReal = 1 - A * rSq;
  const numImag = (Math.sqrt(A) / q) * r;
  const denReal = A - rSq;
  const denImag = (Math.sqrt(A) / q) * r;

  const numMagSq = numReal * numReal + numImag * numImag;
  const denMagSq = denReal * denReal + denImag * denImag;

  if (denMagSq === 0) return gain;

  return 20 * Math.log10(A) + 10 * Math.log10(numMagSq / denMagSq);
}

/**
 * Compute the combined magnitude response of multiple EQ bands
 * at a given frequency.
 */
export function combinedMagnitudeDb(f: number, bands: EqBand[]): number {
  let total = 0;
  for (const band of bands) {
    if (!band.enabled || band.gain === 0) continue;
    if (band.type === "PK") {
      total += peakingMagnitudeDb(f, band.frequency, band.gain, band.q);
    } else if (band.type === "LSC") {
      total += lowShelfMagnitudeDb(f, band.frequency, band.gain, band.q);
    } else if (band.type === "HSC") {
      total += highShelfMagnitudeDb(f, band.frequency, band.gain, band.q);
    }
  }
  return total;
}

/**
 * Compute the combined magnitude response at an array of frequencies.
 */
export function combinedMagnitudeCurve(
  frequencies: number[],
  bands: EqBand[]
): Float64Array {
  const result = new Float64Array(frequencies.length);
  for (let i = 0; i < frequencies.length; i++) {
    result[i] = combinedMagnitudeDb(frequencies[i], bands);
  }
  return result;
}

/**
 * Compute the peak gain of a set of EQ bands across a frequency range.
 * Used to determine the preamp value needed to prevent clipping.
 */
export function peakGain(
  bands: EqBand[],
  minFreq: number = 20,
  maxFreq: number = 20000,
  steps: number = 1000
): number {
  let maxGain = 0;
  const logMin = Math.log10(minFreq);
  const logMax = Math.log10(maxFreq);

  for (let i = 0; i <= steps; i++) {
    const f = Math.pow(10, logMin + (logMax - logMin) * i / steps);
    const g = combinedMagnitudeDb(f, bands);
    if (g > maxGain) maxGain = g;
  }

  return maxGain;
}
